/** Header-action builders for the Enquiries pipelines.
 *  Consultancy ('pipeline'):        Filter + Sort + From Enquiry + New Deal.
 *  Workspace   ('workspace-pipeline'): New Deal.
 *  All actions fire window events the pipeline views listen for. No JSX. */

import { Plus, Inbox, SlidersHorizontal, ArrowDownUp, Check } from '@/shared/ui/icons';
import type { MobileHeaderAction } from './types';

/** Board filter/sort state the view publishes via `pipeline:state-change` so
 *  the header can render active labels + checkmarks. `services` carries the
 *  [key, label] list so this platform-layer builder stays decoupled from the
 *  pipeline feature's label enum. */
export interface PipelineHeaderState {
  serviceFilter: string; // '' === all services
  sortKey: 'newest' | 'oldest' | 'value' | 'idle';
  services: [string, string][];
}

export interface PipelineHeaderCtx {
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
  /** Null until the view mounts and publishes its filter/sort state. */
  pipelineHeaderState?: PipelineHeaderState | null;
}

const SORT_LABELS: Record<PipelineHeaderState['sortKey'], string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  value: 'Highest value',
  idle: 'Most idle',
};
const SORT_ORDER: PipelineHeaderState['sortKey'][] = ['newest', 'oldest', 'value', 'idle'];

/** Consultancy enquiry pipeline — currentView === 'pipeline'.
 *  Owns: Filter, Sort, "From Enquiry" (convert a website enquiry), "New Deal". */
export function buildPipelineHeaderActions(ctx: PipelineHeaderCtx): void {
  const { trailingActions, pushNotificationsAction, pipelineHeaderState } = ctx;

  // Filter + Sort menus — only once the view has published its board state.
  if (pipelineHeaderState) {
    const { serviceFilter, sortKey, services } = pipelineHeaderState;
    const activeServiceLabel = services.find(([k]) => k === serviceFilter)?.[1];

    trailingActions.push({
      id: 'pipeline-filter',
      icon: SlidersHorizontal,
      label: serviceFilter ? activeServiceLabel : undefined,
      ariaLabel: serviceFilter ? `Filter: ${activeServiceLabel}` : 'Filter by service',
      group: 'page',
      subActions: [
        {
          id: 'pipeline-filter-all',
          label: 'All services',
          trailingIcon: serviceFilter === '' ? Check : undefined,
          onClick: () => window.dispatchEvent(
            new CustomEvent('pipeline:set-service', { detail: { service: '' } }),
          ),
        },
        ...services.map(([key, label]) => ({
          id: `pipeline-filter-${key}`,
          label,
          trailingIcon: serviceFilter === key ? Check : undefined,
          onClick: () => window.dispatchEvent(
            new CustomEvent('pipeline:set-service', { detail: { service: key } }),
          ),
        })),
      ],
    });

    trailingActions.push({
      id: 'pipeline-sort',
      icon: ArrowDownUp,
      label: SORT_LABELS[sortKey],
      ariaLabel: `Sort: ${SORT_LABELS[sortKey]}`,
      group: 'page',
      subActions: SORT_ORDER.map((s) => ({
        id: `pipeline-sort-${s}`,
        label: SORT_LABELS[s],
        trailingIcon: sortKey === s ? Check : undefined,
        onClick: () => window.dispatchEvent(
          new CustomEvent('pipeline:set-sort', { detail: { sort: s } }),
        ),
      })),
    });
  }

  trailingActions.push({
    id: 'pipeline-convert-enquiry',
    icon: Inbox,
    ariaLabel: 'New deal from website enquiry',
    onClick: () => window.dispatchEvent(new CustomEvent('pipeline:open-convert')),
    group: 'page',
  });
  trailingActions.push({
    id: 'pipeline-add',
    icon: Plus,
    ariaLabel: 'New deal',
    onClick: () => window.dispatchEvent(new CustomEvent('pipeline:create-deal')),
    group: 'page',
  });

  pushNotificationsAction();
}

/** Workspace bid/tender pipeline — currentView === 'workspace-pipeline'.
 *  Owns: "New Deal". */
export function buildWorkspacePipelineHeaderActions(ctx: PipelineHeaderCtx): void {
  const { trailingActions, pushNotificationsAction } = ctx;

  trailingActions.push({
    id: 'workspace-pipeline-add',
    icon: Plus,
    ariaLabel: 'New deal',
    onClick: () => window.dispatchEvent(new CustomEvent('workspace-pipeline:create-deal')),
    group: 'page',
  });

  pushNotificationsAction();
}
